// src/Pages/Purchase/PurchaseData/ViewPurchaseOrders.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API_URL } from "../../../config";
import "./ViewPurchaseOrders.css";

const ViewPurchaseOrders = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [po, setPo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPO = async () => {
      try {
        // CORRECT ENDPOINT — same as Invoice uses
        const res = await fetch(`${API_URL}/api/purchaseorders/${id}`, {
          credentials: "include",
        });

        if (!res.ok) {
          const text = await res.text();
          console.error("Server returned:", text.substring(0, 300));
          throw new Error(`HTTP ${res.status}`);
        }

        const data = await res.json();

        if (data.success && data.data) {
          setPo(data.data);
        } else {
          console.error("PO not found in response:", data);
          alert("Purchase Order not found");
          navigate("/purchase/purchase-orders");
        }
      } catch (err) {
        console.error("Failed to fetch Purchase Order:", err);
        alert("Error loading Purchase Order");
        navigate("/purchase/purchase-orders");
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchPO();
  }, [id, navigate]);

  if (loading) return <div className="loading">Loading Purchase Order...</div>;
  if (!po) return <div className="error">Purchase Order not found</div>;

  // Calculations
  const subtotal = po.items.reduce((sum, item) => sum + (item.amount || 0), 0);
  const discountAmount =
    po.discountType === "%"
      ? (subtotal * (po.discountValue || 0)) / 100
      : po.discountValue || 0;
  const tdsTcsAmount = po.tdsTcs
    ? (subtotal * (po.tdsTcsPercent || 0)) / 100
    : 0;
  const total = subtotal - discountAmount - tdsTcsAmount + (po.adjustment || 0);

  const formatDate = (date) =>
    date ? new Date(date).toLocaleDateString("en-IN") : "—";

  return (
    <div className="view-po-page">
      <div className="po-print-container">
        {/* Header */}
        <div className="po-header-section">
          <div className="company-details">
            <h1>Your Company Name</h1>
            <p>123 Business Street, City, State - 400001</p>
            <p>GSTIN: 22AAAAA0000A1Z5 | Email: info@yourcompany.com</p>
          </div>
          <div className="po-title">
            <h1>PURCHASE ORDER</h1>
            <h2>{po.poNo}</h2>
          </div>
        </div>

        {/* Meta Info */}
        <div className="po-meta-grid">
          <div>
            <strong>PO Date</strong>
            <br />
            {formatDate(po.date)}
          </div>
          <div>
            <strong>Delivery Date</strong>
            <br />
            {formatDate(po.deliveryDate)}
          </div>
          <div>
            <strong>Reference #</strong>
            <br />
            {po.referenceNo || "—"}
          </div>
          <div>
            <strong>Status</strong>
            <br />
            <span
              className={`status-badge ${
                po.status === "SENT"
                  ? "bg-blue-100 text-blue-800"
                  : "bg-gray-100 text-gray-800"
              }`}
            >
              {po.status || "DRAFT"}
            </span>
          </div>
        </div>

        {/* Vendor & Delivery Address */}
        <div className="vendor-delivery-section">
          <div>
            <strong>Vendor</strong>
            <br />
            <strong>{po.vendorName}</strong>
          </div>
          <div>
            <strong>Deliver To</strong>
            <br />
            {po.deliveryType === "organization"
              ? "Our Organization"
              : po.deliveryTo || "—"}
          </div>
        </div>

        {/* Items Table */}
        <table className="items-table-print">
          <thead>
            <tr>
              <th>ITEM DESCRIPTION</th>
              <th className="text-center">QTY</th>
              <th className="text-right">RATE</th>
              <th className="text-right">AMOUNT</th>
            </tr>
          </thead>
          <tbody>
            {po.items.map((item, i) => (
              <tr key={i}>
                <td>{item.description || item.itemName || "—"}</td>
                <td className="text-center">
                  {item.qty || item.quantity || 0}
                </td>
                <td className="text-right">₹{(item.rate || 0).toFixed(2)}</td>
                <td className="text-right">₹{(item.amount || 0).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals */}
        <div className="totals-section">
          <div className="totals-left">
            <div>Subtotal</div>
            {discountAmount > 0 && (
              <div>
                Discount ({po.discountValue}
                {po.discountType})
              </div>
            )}
            {tdsTcsAmount > 0 && <div>TDS/TCS ({po.tdsTcsPercent}%)</div>}
            {(po.adjustment || 0) !== 0 && <div>Adjustment</div>}
            <div className="grand-total">Total Amount</div>
          </div>
          <div className="totals-right">
            <div>₹{subtotal.toFixed(2)}</div>
            {discountAmount > 0 && <div>-₹{discountAmount.toFixed(2)}</div>}
            {tdsTcsAmount > 0 && <div>-₹{tdsTcsAmount.toFixed(2)}</div>}
            {(po.adjustment || 0) !== 0 && (
              <div>₹{(po.adjustment || 0).toFixed(2)}</div>
            )}
            <div className="grand-total">₹{total.toFixed(2)}</div>
          </div>
        </div>

        {/* Terms & Conditions */}
        {po.termsConditions && (
          <div className="terms-section">
            <strong>Terms & Conditions</strong>
            <div className="mt-2 text-sm whitespace-pre-line">
              {po.termsConditions}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="action-buttons-print">
          <button onClick={() => window.print()} className="print-btn">
            Print / Save as PDF
          </button>
          <button
            onClick={() => navigate("/purchase/purchase-orders")}
            className="back-btn"
          >
            ← Back to List
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewPurchaseOrders;
